import React from 'react';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { BusinessForm } from '@/components/profile/business-form';
import { useSWRConfig } from 'swr';
import type { Business } from '@/types';
import { useUser } from '@/hooks/use-user';
import { useCommunity } from '@/hooks/use-community';
import { AccountHeader } from '@/components/layouts/AccountHeader';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { revalidateAfterCommunityBusinessListingChange } from '@/lib/revalidate-cache';
import { isCommunityId } from '@/lib/community-route';

export default function CreateBusinessPage() {
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const location = useLocation();
  const { user, isLoading: isUserLoading } = useUser();
  const { communityId: communityRouteSegment, businessId } = useParams<{
    communityId?: string;
    businessId?: string;
  }>();

  const { community: communityData, isLoading: isCommunityLoading } =
    useCommunity(communityRouteSegment);
  const resolvedCommunityId =
    communityData?.community?.id ??
    (communityRouteSegment && isCommunityId(communityRouteSegment)
      ? communityRouteSegment
      : undefined);

  const initialDataFromState = (location.state as { initialData?: Business } | null)?.initialData;
  const initialDataFromUser = businessId
    ? user?.businesses?.find((b) => b.id === businessId)
    : undefined;
  const initialData: Business | undefined = initialDataFromState ?? initialDataFromUser;
  const isGlobalPage = !communityRouteSegment;
  const isEditRoute = Boolean(businessId);

  const missingBusinessForEdit =
    isEditRoute && !isUserLoading && user && !initialDataFromState && !initialDataFromUser;
  const showEditLoader = isEditRoute && !initialData && isUserLoading;
  const waitingForCommunityId =
    Boolean(communityRouteSegment) && !isEditRoute && !resolvedCommunityId && isCommunityLoading;

  const content = missingBusinessForEdit ? (
    <div className="max-w-lg mx-auto py-16 px-4 text-center space-y-4">
      <p className="text-muted-foreground">
        This business could not be loaded for editing. You may not be linked to it anymore, or the
        link is invalid.
      </p>
      <Button asChild variant="outline" className="rounded-xl">
        <Link to={communityRouteSegment ? `/${communityRouteSegment}/dashboard` : '/dashboard'}>
          Back to dashboard
        </Link>
      </Button>
    </div>
  ) : (
    <div className="max-w-3xl mx-auto py-6 px-4">
      {showEditLoader || waitingForCommunityId ? (
        <div className="flex justify-center py-24">
          <Loader2 className="h-10 w-10 animate-spin text-primary opacity-70" />
        </div>
      ) : (
        <>
          <div className="mb-6 space-y-1">
            <h1 className="text-2xl font-black font-display tracking-tight">
              {isEditRoute ? 'Edit Business' : 'Create Business'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isEditRoute
                ? 'Update all sections of your business profile.'
                : 'Add a business profile to share with your communities.'}
            </p>
          </div>
          <BusinessForm
            key={initialData?.id ?? 'new'}
            initialData={initialData}
            linkToCommunityId={!isEditRoute ? resolvedCommunityId : undefined}
            onSuccess={async (business) => {
              await mutate('/api/user');
              if (resolvedCommunityId && !isEditRoute) {
                await revalidateAfterCommunityBusinessListingChange(mutate, resolvedCommunityId);
              }
              const profilePath = communityRouteSegment
                ? `/${communityRouteSegment}/b/${business.id}`
                : `/b/${business.id}`;
              navigate(profilePath, { replace: true });
            }}
          />
        </>
      )}
    </div>
  );

  if (isGlobalPage && user) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AccountHeader user={user} />
        <main className="flex-1 overflow-y-auto w-full">{content}</main>
      </div>
    );
  }

  return content;
}
